import { useState, useEffect } from "react"
import { useLocation } from "react-router-dom"
import { lumi } from "../lib/lumi"

/**
 * SEO 資料介面
 */
export interface SEOData {
  title?: string
  description?: string
  keywords?: string
  ogImage?: string
  ogTitle?: string
  ogDescription?: string
  canonical?: string
  alternates?: Array<{ hreflang: string; href: string }>
  // 可擴展其他 meta 標籤
}

/**
 * 動態 SEO Hook
 * 根據當前路徑自動從資料庫載入 SEO 設定
 * 如果資料庫沒有資料，返回提供的預設值
 * 
 * @param defaultSEO - 預設 SEO 資料（當資料庫沒有資料時使用）
 * @returns SEO 資料物件
 */
export const useDynamicSEO = (defaultSEO: SEOData = {}): SEOData => {
  const location = useLocation()
  
  // 🌐 自動生成 Alternate 連結 (hreflang)
  const currentPath = location.pathname
  // 移除 /zh 或 /en 前綴
  const baseRoute = currentPath.replace(/^\/(zh|en)(\/|$)/, '/').replace(/\/$/, '') || '/'
  const domain = 'https://www.ongucoffee.com'
  
  // 生成各語言連結
  const zhUrl = `${domain}/zh${baseRoute === '/' ? '' : baseRoute}`
  const enUrl = `${domain}/en${baseRoute === '/' ? '' : baseRoute}`
  
  const alternates = [
    { hreflang: 'zh-TW', href: zhUrl },
    { hreflang: 'en-US', href: enUrl },
    { hreflang: 'x-default', href: zhUrl }
  ]

  const [seoData, setSeoData] = useState<SEOData>({ ...defaultSEO, alternates })
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // 每次路徑改變時，更新 alternates
    setSeoData(prev => ({ ...prev, alternates }))
  }, [location.pathname])

  useEffect(() => {
    const loadSEO = async () => {
      try {
        // ... keep existing code
        
        // 🔧 安全檢查：確保 page_seo entity 存在
        if (!lumi.entities.page_seo) {
          console.warn("⚠️ page_seo entity 不存在，使用預設 SEO")
          setSeoData(defaultSEO)
          setLoading(false)
          return
        }

        // 查詢當前路徑的 SEO 設定
        const result = await lumi.entities.page_seo.list({
          filter: { 
            url: location.pathname, 
            status: "active" 
          },
          limit: 1
        })

        if (result.list && result.list.length > 0) {
          const dbSEO = result.list[0]
          // ... keep existing code
          
          // 🖼️ OG Image 優先級：上傳字段 > headContent 解析 > 預設值
          const uploadedOgImage = dbSEO?.ogImage && dbSEO.ogImage.trim() !== '' ? dbSEO.ogImage : null
          const parsedOgImage = !uploadedOgImage ? parseMetaContent(dbSEO?.headContent, 'og:image') : null
          const finalOgImage = uploadedOgImage || parsedOgImage || defaultSEO?.ogImage || ''
          
          // ... keep existing code
          
          // 從資料庫讀取 SEO 資料，並合併預設值（使用可選鏈和空值合併防止 undefined 錯誤）
          setSeoData({
            title: dbSEO?.pageName ?? defaultSEO?.title ?? '',
            description: parseMetaContent(dbSEO?.headContent, 'description') ?? defaultSEO?.description ?? '',
            keywords: parseMetaContent(dbSEO?.headContent, 'keywords') ?? defaultSEO?.keywords ?? '',
            ogImage: finalOgImage,
            ogTitle: parseMetaContent(dbSEO?.headContent, 'og:title') ?? dbSEO?.pageName ?? defaultSEO?.ogTitle ?? defaultSEO?.title ?? '',
            ogDescription: parseMetaContent(dbSEO?.headContent, 'og:description') ?? defaultSEO?.ogDescription ?? defaultSEO?.description ?? '',
            canonical: defaultSEO?.canonical ?? '',
            alternates
          })
        } else {
          // ... keep existing code
          setSeoData({ ...defaultSEO, alternates })
        }
      } catch (error) {
        console.warn("⚠️ 載入 SEO 失敗，使用預設值:", error)
        setSeoData({ ...defaultSEO, alternates })
      } finally {
        setLoading(false)
      }
    }

    loadSEO()
  }, [location.pathname]) // 注意：defaultSEO 不加入依賴，避免無限循環

  return seoData
}

/**
 * 從 HTML 字串中解析 meta 標籤內容
 */
const parseMetaContent = (htmlContent: string | undefined, nameOrProperty: string): string | undefined => {
  if (!htmlContent) return undefined
  
  try {
    const parser = new DOMParser()
    const doc = parser.parseFromString(htmlContent, "text/html")
    
    // 嘗試 name 屬性
    let meta = doc.querySelector(`meta[name="${nameOrProperty}"]`)
    if (meta) {
      return meta.getAttribute('content') || undefined
    }
    
    // 嘗試 property 屬性（OG tags）
    meta = doc.querySelector(`meta[property="${nameOrProperty}"]`)
    if (meta) {
      return meta.getAttribute('content') || undefined
    }
    
    return undefined
  } catch (error) {
    console.warn(`⚠️ 解析 meta 標籤失敗 (${nameOrProperty}):`, error)
    return undefined
  }
}
